import { useState, MouseEvent, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { leadFollowUpsApi, LeadFollowUp } from '../lib/api/leadFollowUpsApi'
import { dealFollowUpsApi, DealFollowUp } from '../lib/api/dealFollowUpsApi'
import { tasksApi } from '../lib/api/tasksApi'
import { leadTasksApi, LeadTask } from '../lib/api/leadTasksApi'
import { dealTasksApi, DealTask } from '../lib/api/dealTasksApi'
import { calendarApi, UnifiedCalendarEvent } from '../lib/api/calendarApi'
import { salesApi } from '../lib/api/salesApi'
import { retentionApi } from '../lib/api/retentionApi'
import { dealsApi } from '../lib/api/dealsApi'
import {
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Avatar,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material'
import { FormInput, FormSelect } from '../components/ui'
import { EntityModal } from '../components/shared/EntityModal'
import { toast } from 'react-toastify'
import {
  KeyboardArrowLeft as PrevIcon,
  KeyboardArrowRight as NextIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Event as EventIcon,
  Close as CloseIcon,
  CalendarTodayOutlined,
  OpenInNew as LinkIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { themeColors } from '../app/layout/theme'
import { useOrgSelector } from '../context/OrgSelectorContext'
import { lobsApi } from '../lib/api/tenantsApi'
import { usersAdminApi } from '../lib/api/usersAdminApi'
import { authStore } from '../lib/stores/authStore'
import {
  UnifiedFilterDialog,
  UnifiedFilters,
  FilterDef,
  countActiveFilters,
  applyUnifiedDateFilter,
  EMPTY_UNIFIED_FILTERS,
} from '../components/shared/UnifiedFilterDialog'
import {
  FilterList as FilterListIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material'
import {
  format, addDays, subDays,
  startOfWeek, endOfWeek, eachDayOfInterval, isSameDay,
  addWeeks, subWeeks,
  startOfMonth, endOfMonth, addMonths, subMonths,
} from 'date-fns'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CalendarEvent {
  id: string
  title: string
  date: string      // "YYYY-MM-DD"
  startTime: string      // "HH:mm"
  endTime: string      // "HH:mm"
  color: string
  colorDark: string
  readonly?: boolean
  type?: 'FOLLOW_UP' | 'DEAL_FOLLOW_UP' | 'LEAD_TASK' | 'DEAL_TASK' | 'TASK' | 'LOCAL'
  source?: 'LEAD' | 'DEAL' | 'SALES' | 'RETENTION' | 'CALENDAR'    // where the event came from
  isDone?: boolean
  originalData?: LeadFollowUp | LeadTask | DealFollowUp | DealTask | any
  reminderMinutes?: number
  priority?: string
}

function getPriorityChipConfig(priorityStr?: string) {
  if (!priorityStr) return null;
  const p = priorityStr.toUpperCase();
  if (p === 'LOW') {
    return { label: 'Low', color: '#6b7280', bg: '#f3f4f6' };
  }
  if (p === 'MEDIUM') {
    return { label: 'Medium', color: '#f59e0b', bg: '#fef3c7' };
  }
  if (p === 'HIGH') {
    return { label: 'High', color: '#f97316', bg: '#fff7ed' };
  }
  if (p === 'URGENT') {
    return { label: 'Urgent', color: '#dc2626', bg: '#fef2f2' };
  }
  return null;
}

const TASK_COLORS = { color: '#dcfce7', colorDark: '#22c55e' } // Green
const EVENT_COLORS = { color: '#dbeafe', colorDark: '#3b82f6' } // Blue
const CALL_COLORS = { color: '#ffedd5', colorDark: '#f97316' } // Orange
const MEETING_COLORS = { color: '#f3e8ff', colorDark: '#a855f7' } // Purple

const FOLLOW_UP_COLORS: Record<string, { color: string; colorDark: string }> = {
  CALL: CALL_COLORS,
  MEETING: MEETING_COLORS,
  DEMO: EVENT_COLORS,
  SITE_VISIT: EVENT_COLORS,
  OTHER: EVENT_COLORS,
}

function getCategoryChipConfig(ev: CalendarEvent) {
  if (ev.type === 'LEAD_TASK' || ev.type === 'DEAL_TASK' || ev.type === 'TASK') {
    return { label: 'Task', color: '#16a34a', bg: '#f0fdf4' }
  }
  if (ev.type === 'FOLLOW_UP' || ev.type === 'DEAL_FOLLOW_UP') {
    const originalType = ev.originalData?.type
    if (originalType === 'MEETING') {
      return { label: 'Meeting', color: '#7c3aed', bg: '#f5f3ff' }
    }
    if (originalType === 'CALL') {
      return { label: 'Call', color: '#ea580c', bg: '#fff7ed' }
    }
    return { label: 'Event', color: '#2563eb', bg: '#eff6ff' }
  }
  return { label: 'Event', color: '#2563eb', bg: '#eff6ff' }
}

function unifiedToCalendarEvent(item: UnifiedCalendarEvent): CalendarEvent {
  const start = new Date(item.scheduledAt)
  const end = item.endTime ? new Date(item.endTime) : new Date(start.getTime() + 30 * 60 * 1000)

  let colorConfig = EVENT_COLORS
  if (item.eventType === 'TASK') {
    colorConfig = TASK_COLORS
  } else if (item.category === 'CALL') {
    colorConfig = CALL_COLORS
  } else if (item.category === 'MEETING') {
    colorConfig = MEETING_COLORS
  } else {
    colorConfig = FOLLOW_UP_COLORS[item.category] ?? EVENT_COLORS
  }

  const typeName = item.eventType === 'TASK'
    ? (item.source === 'LEAD' ? 'LEAD_TASK' : item.source === 'DEAL' ? 'DEAL_TASK' : 'TASK')
    : (item.source === 'LEAD' ? 'FOLLOW_UP' : item.source === 'DEAL' ? 'DEAL_FOLLOW_UP' : 'FOLLOW_UP')

  return {
    id: item.id,
    title: item.title ?? item.category,
    date: format(start, 'yyyy-MM-dd'),
    startTime: format(start, 'HH:mm'),
    endTime: format(end, 'HH:mm'),
    color: colorConfig.color,
    colorDark: colorConfig.colorDark,
    readonly: true,
    type: typeName as any,
    source: item.source as any,
    isDone: item.status === 'DONE' || item.status === 'CANCELLED',
    originalData: {
      ...item,
      type: item.category,
      leadId: item.source === 'LEAD' ? item.sourceId : undefined,
      dealId: item.source === 'DEAL' ? item.sourceId : undefined,
      salesId: item.source === 'SALES' ? item.sourceId : undefined,
      retentionCustomerId: item.source === 'RETENTION' ? item.sourceId : undefined,
      leadName: item.source === 'LEAD' ? item.sourceName : undefined,
      dealName: item.source === 'DEAL' ? item.sourceName : undefined,
      salesName: item.source === 'SALES' ? item.sourceName : undefined,
      customerName: item.source === 'RETENTION' ? item.sourceName : undefined,
    },
    reminderMinutes: item.reminderMinutes ?? undefined,
    priority: item.priority ?? undefined,
  }
}

function followUpToCalendarEvent(fu: LeadFollowUp): CalendarEvent {
  const start = new Date(fu.scheduledAt)
  const end = fu.endTime ? new Date(fu.endTime) : new Date(start.getTime() + 60 * 60 * 1000)
  const colors = FOLLOW_UP_COLORS[fu.type] ?? FOLLOW_UP_COLORS.OTHER
  return {
    id: fu.id,
    title: fu.title ?? (fu.type.charAt(0) + fu.type.slice(1).toLowerCase()),
    date: format(start, 'yyyy-MM-dd'),
    startTime: format(start, 'HH:mm'),
    endTime: format(end, 'HH:mm'),
    color: colors.color,
    colorDark: colors.colorDark,
    readonly: true,
    type: 'FOLLOW_UP',
    source: 'LEAD',
    isDone: fu.status === 'DONE' || fu.status === 'CANCELLED',
    originalData: fu,
  }
}

function dealFollowUpToCalendarEvent(fu: DealFollowUp): CalendarEvent {
  const start = new Date(fu.scheduledAt)
  const end = fu.endTime ? new Date(fu.endTime) : new Date(start.getTime() + 60 * 60 * 1000)
  const colors = FOLLOW_UP_COLORS[fu.type] ?? FOLLOW_UP_COLORS.OTHER
  return {
    id: `deal-${fu.id}`,
    title: fu.title ?? (fu.type.charAt(0) + fu.type.slice(1).toLowerCase()),
    date: format(start, 'yyyy-MM-dd'),
    startTime: format(start, 'HH:mm'),
    endTime: format(end, 'HH:mm'),
    color: colors.color,
    colorDark: colors.colorDark,
    readonly: true,
    type: 'DEAL_FOLLOW_UP',
    source: 'DEAL',
    isDone: fu.status === 'DONE' || fu.status === 'CANCELLED',
    originalData: fu,
  }
}

function leadTaskToCalendarEvent(task: LeadTask): CalendarEvent {
  const start = task.dueDate ? new Date(task.dueDate) : new Date()
  const end = new Date(start.getTime() + 30 * 60 * 1000)
  return {
    id: task.id,
    title: task.title,
    date: format(start, 'yyyy-MM-dd'),
    startTime: format(start, 'HH:mm'),
    endTime: format(end, 'HH:mm'),
    color: TASK_COLORS.color,
    colorDark: TASK_COLORS.colorDark,
    readonly: true,
    type: 'LEAD_TASK',
    source: 'LEAD',
    isDone: task.status === 'DONE' || task.status === 'CANCELLED',
    originalData: task,
    priority: task.priority,
  }
}

function dealTaskToCalendarEvent(task: DealTask): CalendarEvent {
  const start = task.dueDate ? new Date(task.dueDate) : new Date()
  const end = new Date(start.getTime() + 30 * 60 * 1000)
  return {
    id: task.id,
    title: task.title,
    date: format(start, 'yyyy-MM-dd'),
    startTime: format(start, 'HH:mm'),
    endTime: format(end, 'HH:mm'),
    color: TASK_COLORS.color,
    colorDark: TASK_COLORS.colorDark,
    readonly: true,
    type: 'DEAL_TASK',
    source: 'DEAL',
    isDone: task.status === 'DONE' || task.status === 'CANCELLED',
    originalData: task,
    priority: task.priority,
  }
}

// ── Constants & Helpers ───────────────────────────────────────────────────────

const HOUR_HEIGHT = 80
const TIME_COLUMN_WIDTH = 64
const START_HOUR = 0
const END_HOUR = 23
const TOP_OFFSET = 20

const PREDEFINED_COLORS = [
  { label: 'Yellow', bg: '#FFEAA7', border: '#E6A23C' },
  { label: 'Green', bg: '#D1FFDF', border: '#2ECC71' },
  { label: 'Blue', bg: '#D4E6F1', border: '#3498DB' },
  { label: 'Purple', bg: '#E8DAEF', border: '#9B59B6' },
  { label: 'Red', bg: '#FADBD8', border: '#E74C3C' },
]

function parseTimeToDecimal(timeStr: string): number {
  if (!timeStr) return 0
  const [h, m] = timeStr.split(':').map(Number)
  return h + m / 60
}

function formatTimeDisplay(timeStr: string): string {
  if (!timeStr) return ''
  const [hStr, mStr] = timeStr.split(':')
  let h = parseInt(hStr, 10)
  const ampm = h >= 12 ? 'pm' : 'am'
  h = h % 12 || 12
  return `${h}:${mStr}${ampm}`
}

function generateTimeOptions() {
  const opts: string[] = []
  for (let i = START_HOUR; i <= END_HOUR + 2; i++) {
    const hh = i.toString().padStart(2, '0')
    opts.push(`${hh}:00`)
    opts.push(`${hh}:30`)
  }
  return opts
}
const TIME_OPTIONS = generateTimeOptions()

// ── Event Detail Modal ────────────────────────────────────────────────────────

function AuditUserRow({ label, user }: { label: string; user: { name: string; email: string; avatarUrl: string | null } | null }) {
  if (!user) return null
  return (
    <Box>
      <Typography fontSize="11px" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px" gutterBottom>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar src={user.avatarUrl ?? undefined} sx={{ width: 24, height: 24, fontSize: 11, bgcolor: themeColors.primary }}>
          {user.name[0]?.toUpperCase()}
        </Avatar>
        <Typography fontSize="13px" fontWeight={600}>{user.name}</Typography>
      </Box>
    </Box>
  )
}

// ── Complete Event Modal (Note Dialog) ────────────────────────────────────────

function CompleteEventModal({
  open,
  eventTitle,
  onClose,
  onComplete,
  isCompleting,
}: {
  open: boolean
  eventTitle: string
  onClose: () => void
  onComplete: (outcome: string) => void
  isCompleting: boolean
}) {
  const [outcome, setOutcome] = useState('')
  function handleClose() { setOutcome(''); onClose() }
  function handleSubmit() { onComplete(outcome); setOutcome('') }
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #eee' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#16a34a' }} />
          <Typography fontWeight={700} fontSize="15px">Mark Event as Done</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <Typography variant="body2" sx={{ color: '#374151', mb: 1.5 }}>
          Briefly describe the outcome of <strong>{eventTitle}</strong>:
        </Typography>
        <Box
          component="textarea"
          value={outcome}
          onChange={(e: any) => setOutcome(e.target.value)}
          placeholder="e.g. Client agreed to proceed, follow-up next week…"
          rows={3}
          sx={{
            width: '100%', boxSizing: 'border-box', resize: 'vertical',
            borderRadius: '8px', border: '1.5px solid #e5e7eb', p: 1.5,
            fontSize: '13px', fontFamily: 'inherit', lineHeight: 1.6,
            '&:focus': { outline: 'none', borderColor: '#7c3aed' },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} variant="text" sx={{ borderRadius: '8px', textTransform: 'none', color: 'text.secondary' }}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isCompleting}
          sx={{ borderRadius: '8px', bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' }, textTransform: 'none', fontWeight: 600 }}
        >
          {isCompleting ? 'Saving…' : 'Mark Done'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Source Badge ─────────────────────────────────────────────────────────────

function SourceBadge({ source }: { source?: string }) {
  if (!source) return null
  const cfg =
    source === 'LEAD'      ? { label: 'Lead',      bg: '#eff6ff', color: '#2563eb' } :
    source === 'DEAL'      ? { label: 'Deal',      bg: '#fefce8', color: '#ca8a04' } :
    source === 'SALES'     ? { label: 'Sales',     bg: '#ecfdf5', color: '#059669' } :
    source === 'RETENTION' ? { label: 'Retention', bg: '#fdf2f8', color: '#db2777' } :
                             { label: 'Reminder',  bg: '#f5f3ff', color: '#7c3aed' }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: 0.25, borderRadius: '20px', bgcolor: cfg.bg, border: `1px solid ${cfg.color}30` }}>
      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: cfg.color, letterSpacing: '0.3px' }}>{cfg.label}</Typography>
    </Box>
  )
}

function EventDetailModal({
  open,
  onClose,
  event,
}: {
  open: boolean
  onClose: () => void
  event: CalendarEvent | null
}) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [completeOpen, setCompleteOpen] = useState(false)

  const data = event?.originalData
  const isFollowUp = event?.type === 'FOLLOW_UP' || event?.originalData?.eventType === 'FOLLOW_UP' || (data?.category && data?.category !== 'TASK')
  const isTask = event?.type === 'TASK' || event?.originalData?.eventType === 'TASK' || data?.category === 'TASK' || event?.type === 'LEAD_TASK' || event?.type === 'DEAL_TASK'
  const isLeadTask = event?.type === 'LEAD_TASK'
  const isDealTask = event?.type === 'DEAL_TASK'

  const completeTaskMutation = useMutation({
    mutationFn: async () => {
      const src = event?.source
      const taskId = event?.id ?? ''
      const realId = taskId.startsWith('deal-') ? taskId.slice(5) : taskId
      const parentId = data?.sourceId || data?.leadId || data?.dealId || data?.salesId || data?.retentionCustomerId

      if (src === 'SALES' && parentId) {
        return salesApi.updateTask(parentId, realId, { status: 'DONE' })
      }
      if (src === 'RETENTION' && parentId) {
        return retentionApi.updateTask(parentId, realId, { status: 'DONE' })
      }
      if (src === 'DEAL' && parentId) {
        return dealsApi.updateTask(parentId, realId, { title: data?.title || event?.title || 'Task', status: 'DONE' })
      }
      if (src === 'LEAD' && parentId) {
        return leadTasksApi.update(parentId, realId, { status: 'DONE' })
      }
      return tasksApi.complete(realId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['unified-calendar-events'] })
      qc.invalidateQueries({ queryKey: ['calendar-tasks'] })
      qc.invalidateQueries({ queryKey: ['tasks'] })
      onClose()
    },
  })

  const completeFollowUpMutation = useMutation({
    mutationFn: async (outcome: string) => {
      const src = event?.source
      const fuId = event?.id ?? ''
      const realId = fuId.startsWith('deal-') ? fuId.slice(5) : fuId
      const parentId = data?.sourceId || data?.leadId || data?.dealId || data?.salesId || data?.retentionCustomerId

      if (src === 'SALES' && parentId) {
        return salesApi.completeFollowUp(parentId, realId, outcome)
      }
      if (src === 'RETENTION' && parentId) {
        return retentionApi.completeFollowUp(parentId, realId, outcome)
      }
      if (src === 'DEAL' && parentId) {
        return dealFollowUpsApi.complete(parentId, realId, { outcome })
      }
      return leadFollowUpsApi.complete(parentId ?? data?.leadId ?? '', realId, { outcome })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['unified-calendar-events'] })
      setCompleteOpen(false)
      onClose()
    },
  })

  const cancelFollowUpMutation = useMutation({
    mutationFn: async () => {
      const src = event?.source
      const fuId = event?.id ?? ''
      const realId = fuId.startsWith('deal-') ? fuId.slice(5) : fuId
      const parentId = data?.sourceId || data?.leadId || data?.dealId || data?.salesId || data?.retentionCustomerId

      if (src === 'SALES' && parentId) {
        return salesApi.cancelFollowUp(parentId, realId)
      }
      if (src === 'RETENTION' && parentId) {
        return retentionApi.cancelFollowUp(parentId, realId)
      }
      if (src === 'DEAL' && parentId) {
        return dealFollowUpsApi.cancel(parentId, realId)
      }
      return leadFollowUpsApi.cancel(parentId ?? data?.leadId ?? '', realId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['unified-calendar-events'] })
      onClose()
    },
  })

  if (!event) return null

  const isDone = data?.status === 'DONE' || data?.status === 'CANCELLED'
  const leadId = data?.leadId
  const dealId = data?.dealId
  const leadName = data?.leadName ?? null
  const dealName = data?.dealName ?? null
  const createdBy = data?.createdBy ?? null
  const assignedTo = data?.assignedTo ?? null

  const typeLabel = isFollowUp ? 'Follow-up' : isTask ? 'Task' : 'Event'

  const isCompleting = completeFollowUpMutation.isPending || cancelFollowUpMutation.isPending || completeTaskMutation.isPending

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #eee' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: event.colorDark }} />
            <Typography fontWeight={700} fontSize="15px">{typeLabel} Details</Typography>
            <SourceBadge source={event.source ?? (event.type === 'LOCAL' ? 'CALENDAR' : undefined)} />
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2}>

            {/* Title */}
            <Box>
              <Typography fontSize="11px" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px" gutterBottom>Title</Typography>
              <Typography fontSize="15px" fontWeight={700} color="#1A1A2E">{event.title}</Typography>
            </Box>

            {/* Date & Time */}
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box>
                <Typography fontSize="11px" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px" gutterBottom>Date</Typography>
                <Typography fontSize="13px" fontWeight={600}>{format(new Date(event.date), 'dd MMM yyyy')}</Typography>
              </Box>
              <Box>
                <Typography fontSize="11px" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px" gutterBottom>Time</Typography>
                <Typography fontSize="13px" fontWeight={600}>{formatTimeDisplay(event.startTime)} – {formatTimeDisplay(event.endTime)}</Typography>
              </Box>
            </Box>

            {/* Type, Priority & Status row */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {/* Type */}
              <Box>
                <Typography fontSize="11px" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px" gutterBottom>Type</Typography>
                <Chip
                  label={data?.type || data?.category || (event.type === 'TASK' || isLeadTask || isDealTask ? 'TASK' : 'EVENT')}
                  size="small"
                  sx={{ fontWeight: 700, bgcolor: event.color, color: event.colorDark, border: 'none' }}
                />
              </Box>

              {/* Priority */}
              {(event.priority || data?.priority) && (
                <Box>
                  <Typography fontSize="11px" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px" gutterBottom>Priority</Typography>
                  {(() => {
                    const p = event.priority || data?.priority
                    const pConfig = getPriorityChipConfig(p)
                    if (!pConfig) return <Chip label={p} size="small" sx={{ fontWeight: 700 }} />
                    return <Chip label={pConfig.label} size="small" sx={{ fontWeight: 700, bgcolor: pConfig.bg, color: pConfig.color, border: 'none' }} />
                  })()}
                </Box>
              )}

              {/* Status */}
              {(data?.status || event.isDone !== undefined) && (
                <Box>
                  <Typography fontSize="11px" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px" gutterBottom>Status</Typography>
                  {(() => {
                    const st = (data?.status || (event.isDone ? 'DONE' : 'OPEN')).toUpperCase()
                    const stColor =
                      st === 'DONE' || st === 'COMPLETED' ? '#16a34a' :
                      st === 'IN_PROGRESS'                ? '#2563eb' :
                      st === 'CANCELLED'                  ? '#6b7280' : '#f59e0b'
                    return (
                      <Chip
                        label={st}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 700, color: stColor, borderColor: `${stColor}40` }}
                      />
                    )
                  })()}
                </Box>
              )}
            </Box>

            {/* Notes & Description */}
            {(data?.notes || data?.description) && (
              <Box>
                <Typography fontSize="11px" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px" gutterBottom>Notes</Typography>
                <Typography fontSize="13px" sx={{ bgcolor: '#f8f9fb', p: 1.5, borderRadius: '8px', border: '1px solid #eee', lineHeight: 1.6 }}>
                  {data.notes || data.description}
                </Typography>
              </Box>
            )}

            {/* Outcome */}
            {data?.outcome && (
              <Box>
                <Typography fontSize="11px" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px" gutterBottom>Outcome</Typography>
                <Typography fontSize="13px" sx={{ bgcolor: '#f0fdf4', p: 1.5, borderRadius: '8px', border: '1px solid #bbf7d0', lineHeight: 1.6, color: '#166534' }}>
                  {data.outcome}
                </Typography>
              </Box>
            )}


            {/* Created by */}
            <AuditUserRow label="Created by" user={createdBy} />

            {/* Parent entity link (Lead / Deal / Sales / Retention) */}
            {(() => {
              const src = event.source
              const srcId = data?.sourceId || data?.leadId || data?.dealId || data?.salesId || data?.retentionCustomerId
              const srcName = data?.sourceName || data?.leadName || data?.dealName || data?.salesName || data?.customerName
              if (!srcId) return null

              const linkPath =
                src === 'LEAD'      ? `/leads/${srcId}` :
                src === 'DEAL'      ? `/deals/${srcId}` :
                src === 'SALES'     ? `/sales/${srcId}` :
                src === 'RETENTION' ? `/customer-retention/${srcId}` : undefined
              
              const label =
                src === 'LEAD'      ? 'Lead' :
                src === 'DEAL'      ? 'Deal' :
                src === 'SALES'     ? 'Sales' :
                src === 'RETENTION' ? 'Retention Customer' : 'Record'

              if (!linkPath) return null

              return (
                <Box>
                  <Typography fontSize="11px" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px" gutterBottom>{label}</Typography>
                  <Box
                    onClick={() => { navigate(linkPath); onClose() }}
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', color: themeColors.primary, '&:hover': { textDecoration: 'underline' } }}
                  >
                    <PersonIcon sx={{ fontSize: 15 }} />
                    <Typography fontSize="13px" fontWeight={700}>{srcName ?? `View ${label}`}</Typography>
                    <LinkIcon sx={{ fontSize: 13 }} />
                  </Box>
                </Box>
              )
            })()}

          </Stack>
        </DialogContent>

        <Box sx={{ px: 3, pb: 3, pt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {!isDone && (
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              {!isTask && (
                <Button
                  onClick={() => cancelFollowUpMutation.mutate()}
                  variant="outlined"
                  fullWidth
                  color="error"
                  disabled={isCompleting}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, py: 1, height: '40px' }}
                >
                  {cancelFollowUpMutation.isPending ? 'Canceling…' : 'Cancel Event'}
                </Button>
              )}
              {isTask ? (
                <Button
                  onClick={() => completeTaskMutation.mutate()}
                  variant="contained"
                  fullWidth
                  disabled={isCompleting}
                  sx={{ borderRadius: '8px', bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' }, textTransform: 'none', fontWeight: 600, py: 1, height: '40px' }}
                >
                  {completeTaskMutation.isPending ? 'Saving…' : 'Mark as Done'}
                </Button>
              ) : (
                <Button
                  onClick={() => setCompleteOpen(true)}
                  variant="contained"
                  fullWidth
                  disabled={isCompleting}
                  sx={{ borderRadius: '8px', bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' }, textTransform: 'none', fontWeight: 600, py: 1, height: '40px' }}
                >
                  {completeFollowUpMutation.isPending ? 'Saving…' : 'Mark as Done'}
                </Button>
              )}
            </Box>
          )}

          {(() => {
            const src = event.source
            const srcId = data?.sourceId || data?.leadId || data?.dealId || data?.salesId || data?.retentionCustomerId
            if (!srcId || !isDone) return null

            const linkPath =
              src === 'LEAD'      ? `/leads/${srcId}` :
              src === 'DEAL'      ? `/deals/${srcId}` :
              src === 'SALES'     ? `/sales/${srcId}` :
              src === 'RETENTION' ? `/customer-retention/${srcId}` : undefined

            const label =
              src === 'LEAD'      ? 'Go to Lead Profile' :
              src === 'DEAL'      ? 'Go to Deal' :
              src === 'SALES'     ? 'Go to Sales Record' :
              src === 'RETENTION' ? 'Go to Retention Customer' : 'Go to Profile'

            const btnColor =
              src === 'LEAD'      ? themeColors.primary :
              src === 'DEAL'      ? '#ca8a04' :
              src === 'SALES'     ? '#059669' :
              src === 'RETENTION' ? '#db2777' : themeColors.primary

            if (!linkPath) return null

            return (
              <Button
                onClick={() => { navigate(linkPath); onClose() }}
                variant="outlined"
                fullWidth
                startIcon={<LinkIcon sx={{ fontSize: 15 }} />}
                sx={{ borderRadius: '8px', borderColor: btnColor, color: btnColor, textTransform: 'none', fontWeight: 600, py: 1, height: '40px', '&:hover': { borderColor: btnColor, bgcolor: `${btnColor}0D` } }}
              >
                {label}
              </Button>
            )
          })()}

          <Button onClick={onClose} variant="text" fullWidth sx={{ borderRadius: '8px', color: 'text.secondary', textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
        </Box>
      </Dialog>

      <CompleteEventModal
        open={completeOpen}
        eventTitle={event?.title ?? ''}
        onClose={() => setCompleteOpen(false)}
        onComplete={(outcome) => {
          completeFollowUpMutation.mutate(outcome)
        }}
        isCompleting={isCompleting}
      />
    </>
  )
}

// ── Day Events Modal ──────────────────────────────────────────────────────────

function DayEventsModal({
  open,
  onClose,
  dateStr,
  events,
  onEventClick,
}: {
  open: boolean
  onClose: () => void
  dateStr: string
  events: CalendarEvent[]
  onEventClick: (event: CalendarEvent) => void
}) {
  const formattedDate = useMemo(() => {
    if (!dateStr) return ''
    try {
      return format(new Date(dateStr), 'dd MMM yyyy')
    } catch {
      return dateStr
    }
  }, [dateStr])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #eee' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayOutlined sx={{ color: themeColors.primary, fontSize: 20 }} />
          <Typography fontWeight={700} fontSize="15px">Events on {formattedDate}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 3 }}>
        {events.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <EventIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
            <Typography fontSize="13px">No events scheduled for this day</Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {events.map(ev => (
              <Box
                key={ev.id}
                onClick={() => {
                  onEventClick(ev)
                  onClose()
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: '10px',
                  bgcolor: ev.color,
                  borderLeft: `4px solid ${ev.colorDark}`,
                  cursor: 'pointer',
                  transition: 'transform 0.15s, filter 0.15s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    filter: 'brightness(0.96)',
                  },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography fontSize="13px" fontWeight={700} color={ev.colorDark}>
                    {ev.title}
                  </Typography>
                  <Typography fontSize="11px" fontWeight={600} color={ev.colorDark} sx={{ opacity: 0.8 }}>
                    {formatTimeDisplay(ev.startTime)} – {formatTimeDisplay(ev.endTime)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ── Create Event Modal ────────────────────────────────────────────────────────

const EVENT_TYPES = [
  { value: 'CALL',       label: 'Call'       },
  { value: 'MEETING',    label: 'Meeting'    },
  { value: 'DEMO',       label: 'Demo'       },
  { value: 'SITE_VISIT', label: 'Site Visit' },
  { value: 'OTHER',      label: 'Other'      },
]

const getCurrentTimeStr = () => {
  const now = new Date()
  const h = now.getHours().toString().padStart(2, '0')
  const m = now.getMinutes().toString().padStart(2, '0')
  return `${h}:${m}`
}

const REMINDER_OPTIONS = [
  { value: '',     label: 'No reminder'       },
  { value: '5',    label: '5 minutes before'  },
  { value: '10',   label: '10 minutes before' },
  { value: '15',   label: '15 minutes before' },
  { value: '30',   label: '30 minutes before' },
  { value: '60',   label: '1 hour before'     },
  { value: '120',  label: '2 hours before'    },
  { value: '1440', label: '1 day before'      },
]

function CreateEventModal({
  open, onClose, onSave, defaultStartTime, defaultEndTime, defaultDate,
}: {
  open: boolean
  onClose: () => void
  onSave: (event: Omit<CalendarEvent, 'id'>) => void
  defaultStartTime: string
  defaultEndTime: string
  defaultDate: string
}) {
  const [title, setTitle] = useState('')
  const [type, setType] = useState('MEETING')
  const [date, setDate] = useState(defaultDate)
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [reminder, setReminder] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('MEDIUM')

  useEffect(() => {
    if (open) {
      setTitle('')
      setType('MEETING')
      setDescription('')
      setReminder('')
      setDate(defaultDate)
      setPriority('MEDIUM')

      const todayStr = format(new Date(), 'yyyy-MM-dd')
      const isPickedToday = defaultDate === todayStr

      if (isPickedToday) {
        const timeNow = getCurrentTimeStr()
        if (defaultStartTime && defaultStartTime >= timeNow) {
          setStartTime(defaultStartTime)
          setEndTime(defaultEndTime || '18:00')
        } else {
          setStartTime(timeNow)
          const now = new Date()
          now.setHours(now.getHours() + 1)
          const h = now.getHours().toString().padStart(2, '0')
          const m = now.getMinutes().toString().padStart(2, '0')
          setEndTime(`${h}:${m}`)
        }
      } else {
        setStartTime(defaultStartTime || '10:00')
        setEndTime(defaultEndTime || '11:00')
      }
    }
  }, [open, defaultStartTime, defaultEndTime, defaultDate])

  const todayStr = format(new Date(), 'yyyy-MM-dd')
  const isSelectedToday = date === todayStr
  const timeNow = getCurrentTimeStr()

  const handleSave = () => {
    if (!title.trim()) { toast.error('Title is required'); return }
    if (!date) { toast.error('Date is required'); return }
    if (!startTime) { toast.error('Start Time is required'); return }
    if (!endTime) { toast.error('End Time is required'); return }

    if (date < todayStr) {
      toast.error('Cannot create events for past dates')
      return
    }

    if (isSelectedToday && startTime < timeNow) {
      toast.error('Start time cannot be in the past')
      return
    }

    if (startTime >= endTime) {
      toast.error('End time must be after start time')
      return
    }

    let color = EVENT_COLORS.color, colorDark = EVENT_COLORS.colorDark
    if (type === 'CALL')       { color = CALL_COLORS.color; colorDark = CALL_COLORS.colorDark }
    if (type === 'MEETING')    { color = MEETING_COLORS.color; colorDark = MEETING_COLORS.colorDark }
    if (type === 'DEMO')       { color = EVENT_COLORS.color; colorDark = EVENT_COLORS.colorDark }
    if (type === 'SITE_VISIT') { color = EVENT_COLORS.color; colorDark = EVENT_COLORS.colorDark }
    if (type === 'OTHER')      { color = EVENT_COLORS.color; colorDark = EVENT_COLORS.colorDark }

    onSave({
      title: title.trim(),
      date,
      startTime,
      endTime,
      color,
      colorDark,
      type: 'LOCAL',
      source: 'CALENDAR',
      reminderMinutes: reminder ? Number(reminder) : undefined,
      priority,
    })
    onClose()
  }

  const isSubmitDisabled =
    !title.trim() ||
    !date ||
    !startTime ||
    !endTime ||
    date < todayStr ||
    (isSelectedToday && startTime < timeNow) ||
    startTime >= endTime

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      onSubmit={handleSave}
      title="New Calendar Event"
      submitLabel="Create Event"
      isSubmitting={false}
      submitDisabled={isSubmitDisabled}
      maxWidth="sm"
      icon={<CalendarTodayOutlined sx={{ fontSize: 18 }} />}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <FormInput
          label="Title"
          labelPosition="above"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Enter title"
          fullWidth
          required
        />

        <FormSelect
          value={EVENT_TYPES.find(t => t.value === type) || type}
          label="Type"
          labelPosition="above"
          onChange={v => {
            const val = typeof v === 'object' && v !== null ? (v as any).value : v
            setType(val)
          }}
          options={EVENT_TYPES}
          fullWidth
          required
        />

        <FormSelect
          value={priority}
          label="Priority"
          labelPosition="above"
          onChange={v => {
            const val = typeof v === 'object' && v !== null ? (v as any).value : v
            setPriority(val)
          }}
          options={[
            { value: 'LOW',    label: 'Low'    },
            { value: 'MEDIUM', label: 'Medium' },
            { value: 'HIGH',   label: 'High'   },
            { value: 'URGENT', label: 'Urgent' },
          ]}
          fullWidth
        />

        <FormInput
          label="Date"
          labelPosition="above"
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          fullWidth
          required
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: todayStr }}
        />

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <FormInput
            label="Start Time"
            labelPosition="above"
            type="time"
            value={startTime}
            onChange={e => setStartTime(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: isSelectedToday ? timeNow : undefined }}
          />
          <FormInput
            label="End Time"
            labelPosition="above"
            type="time"
            value={endTime}
            onChange={e => setEndTime(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: startTime }}
          />
        </Box>

        <FormSelect
          value={reminder}
          label="Reminder"
          labelPosition="above"
          onChange={v => setReminder(v as string)}
          options={REMINDER_OPTIONS}
          fullWidth
        />

        <FormInput
          label="Description"
          labelPosition="above"
          value={description}
          onChange={e => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          placeholder="Type here"
        />
      </Box>
    </EntityModal>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function CalendarPage() {
  const [view, setView] = useState('month')
  const [currentDate, setCurrentDate] = useState(new Date())

  const [localEvents, setLocalEvents] = useState<CalendarEvent[]>(() => {
    try {
      const raw = localStorage.getItem('crm-calendar-events')
      return raw ? JSON.parse(raw) : []
    } catch { return [] }
  })

  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [newStartTime, setNewStartTime] = useState('10:00')
  const [newEndTime, setNewEndTime] = useState('11:00')
  const [modalDateStr, setModalDateStr] = useState('')
  const [dayEventsOpen, setDayEventsOpen] = useState(false)
  const [dayEventsDate, setDayEventsDate] = useState('')

  // ── Unified filters (leads-style) ─────────────────────────────────────────
  // LOB + Users filters: SuperAdmin / OrgAdmin only. SuperAdmin (no orgId, no
  // lobId): LOBs come from the organisation selected in the top-nav switcher.
  // OrgAdmin (orgId, no lobId): LOBs of own org. Normal users are already
  // LOB-scoped by the backend, so they only get Date / Event filters.
  const { selectedOrgId } = useOrgSelector()
  // The backend sometimes sends the zero-GUID for "unset" — treat it as empty
  const ZERO_GUID = '00000000-0000-0000-0000-000000000000'
  const realId = (id?: string | null) => (id && id !== ZERO_GUID ? id : '')
  const userOrgId    = realId(authStore.user?.orgId)
  const userLobId    = realId(authStore.user?.lobId)
  const isSuperAdmin = !userOrgId && !userLobId
  const isOrgAdmin   = !!userOrgId && !userLobId
  const canFilterLob = isSuperAdmin || isOrgAdmin
  const canFilterUsers = canFilterLob || authStore.canViewLobWideData

  const orgIdForLobs = isSuperAdmin ? (selectedOrgId || '') : userOrgId

  const [filters, setFilters] = useState<UnifiedFilters>(EMPTY_UNIFIED_FILTERS)
  const [filterOpen, setFilterOpen] = useState(false)
  const activeFilterCount = countActiveFilters(filters)

  const { data: lobOptions = [] } = useQuery({
    queryKey: ['calendar-lobs', orgIdForLobs],
    queryFn: () => lobsApi.getAll(orgIdForLobs),
    enabled: canFilterLob && !!orgIdForLobs,
    staleTime: 60_000,
    retry: false,
    throwOnError: false,
  })

  // Users list for the Users filter — scoped to the org, further narrowed
  // client-side to the selected LOBs (users based on the LOBs).
  const { data: usersData } = useQuery({
    queryKey: ['calendar-users', orgIdForLobs],
    queryFn: () => usersAdminApi.getAll({ pageSize: 1000, orgId: orgIdForLobs || undefined }),
    enabled: canFilterUsers,
    staleTime: 60_000,
    retry: false,
    throwOnError: false,
  })

  const selectedLobIds = useMemo(
    () => (canFilterLob ? (filters.checkboxes.lobId ?? []) : []),
    [canFilterLob, filters.checkboxes.lobId],
  )

  // Full org-scoped user list — the filter dialog itself narrows this live
  // to the LOB(s) ticked inside the dialog (LOB → Users cascade).
  const userOptions = useMemo(() => usersData?.items ?? [], [usersData])

  // Reset tenant-dependent filters when the org context changes (navbar switch)
  useEffect(() => {
    setFilters(f => ({ ...f, checkboxes: { ...f.checkboxes, lobId: [], userId: [] } }))
  }, [orgIdForLobs])

  const filterDefs: FilterDef[] = useMemo(() => {
    const defs: FilterDef[] = []
    if (canFilterLob) {
      defs.push({
        type: 'checkbox',
        key: 'lobId',
        label: 'LOB',
        options: lobOptions.map(l => ({ label: l.name, value: l.id })),
      })
    }
    if (canFilterUsers) {
      defs.push({
        type: 'checkbox',
        key: 'userId',
        label: 'Users',
        options: userOptions.map(u => ({ label: u.name, value: u.id })),
      })
    }
    defs.push({
      type: 'checkbox',
      key: 'source',
      label: 'Event For',
      options: [
        { label: 'Lead',      value: 'LEAD' },
        { label: 'Deal',      value: 'DEAL' },
        { label: 'Sales',     value: 'SALES' },
        { label: 'Retention', value: 'RETENTION' },
        { label: 'Reminder',  value: 'CALENDAR' },
      ],
    })
    defs.push({
      type: 'checkbox',
      key: 'category',
      label: 'Event Type',
      options: [
        { label: 'Task',       value: 'TASK' },
        { label: 'Call',       value: 'CALL' },
        { label: 'Meeting',    value: 'MEETING' },
        { label: 'Demo',       value: 'DEMO' },
        { label: 'Site Visit', value: 'SITE_VISIT' },
        { label: 'Other',      value: 'OTHER' },
      ],
    })
    defs.push({ type: 'date', label: 'Date' })
    return defs
  }, [canFilterLob, lobOptions, userOptions])

  // LOB filtering is server-side; everything else is applied client-side below
  const lobIdsParam = selectedLobIds.length ? selectedLobIds : undefined
  const lobKey = selectedLobIds.join(',')

  // ── Unified Data fetching (Month window start/end) ────────────────────────
  const calendarRange = useMemo(() => {
    const from = startOfMonth(subMonths(currentDate, 1))
    const to   = endOfMonth(addMonths(currentDate, 1))
    return {
      fromDate: `${format(from, 'yyyy-MM-dd')}T00:00:00Z`,
      toDate:   `${format(to, 'yyyy-MM-dd')}T23:59:59Z`,
    }
  }, [currentDate])

  const { data: unifiedEvents = [] } = useQuery({
    queryKey: ['unified-calendar-events', calendarRange, lobKey, selectedOrgId],
    queryFn: () => calendarApi.getEvents({
      fromDate: calendarRange.fromDate,
      toDate:   calendarRange.toDate,
      orgIds:   selectedOrgId ? [selectedOrgId] : undefined,
      lobIds:   lobIdsParam,
    }),
    staleTime: 60_000,
    retry: false,
    throwOnError: false,
  })

  useEffect(() => {
    try { localStorage.setItem('crm-calendar-events', JSON.stringify(localEvents)) } catch { }
  }, [localEvents])

  const scheduledTimeoutsRef = useRef<Map<string, number>>(new Map())

  const scheduleNotification = (event: CalendarEvent) => {
    if (!event.reminderMinutes) return
    const timeoutsMap = scheduledTimeoutsRef.current
    if (timeoutsMap.has(event.id)) {
      clearTimeout(timeoutsMap.get(event.id))
      timeoutsMap.delete(event.id)
    }

    const eventTimeStr = `${event.date}T${event.startTime}`
    const eventTime = new Date(eventTimeStr).getTime()
    const reminderTime = eventTime - event.reminderMinutes * 60 * 1000
    const delay = reminderTime - Date.now()

    if (delay > 0) {
      const timeoutId = window.setTimeout(() => {
        toast.info(`🔔 Event Reminder: "${event.title}" is starting in ${event.reminderMinutes} minutes!`, {
          autoClose: 10000,
          position: "top-right"
        })

        if (Notification.permission === 'granted') {
          new Notification(`CRM Event Reminder: ${event.title}`, {
            body: `Starting at ${event.startTime} today`,
            icon: '/favicon.ico'
          })
        }
        timeoutsMap.delete(event.id)
      }, delay)

      timeoutsMap.set(event.id, timeoutId)
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission()
      }
    }
  }, [])

  useEffect(() => {
    localEvents.forEach(scheduleNotification)
    return () => {
      const timeoutsMap = scheduledTimeoutsRef.current
      timeoutsMap.forEach(id => clearTimeout(id))
      timeoutsMap.clear()
    }
  }, [localEvents])

  const allEvents = useMemo(
    () => [
      ...unifiedEvents.map(unifiedToCalendarEvent),
      ...localEvents.map(ev => ({ ...ev, source: ev.source ?? ('CALENDAR' as const) })),
    ],
    [unifiedEvents, localEvents],
  )

  // ── Client-side filters: date range, event source, event type, users ──────
  const events = useMemo(() => {
    let result = allEvents

    // Date preset / custom range
    if (filters.datePreset) {
      result = result.filter(e => applyUnifiedDateFilter(`${e.date}T${e.startTime || '00:00'}`, filters))
    }

    // Event For: Lead / Deal / Reminder (local calendar events)
    const sources = filters.checkboxes.source ?? []
    if (sources.length) {
      result = result.filter(e => sources.includes(e.source ?? 'CALENDAR'))
    }

    // Event Type: Task / Call / Meeting / Demo / Site Visit / Other
    const categories = filters.checkboxes.category ?? []
    if (categories.length) {
      result = result.filter(e => {
        const cat = (e.type === 'LEAD_TASK' || e.type === 'DEAL_TASK' || e.type === 'TASK')
          ? 'TASK'
          : (e.originalData?.type ?? 'OTHER')
        return categories.includes(cat)
      })
    }

    // Users: match assigned-to or created-by
    const userIds = filters.checkboxes.userId ?? []
    if (userIds.length) {
      result = result.filter(e => {
        const assignedId = e.originalData?.assignedTo?.id
        const createdId  = e.originalData?.createdBy?.id
        return (assignedId && userIds.includes(assignedId)) || (createdId && userIds.includes(createdId))
      })
    }

    return result
  }, [allEvents, filters])

  const dayEvents = useMemo(() => {
    if (!dayEventsDate) return []
    return events.filter(e => e.date === dayEventsDate)
  }, [events, dayEventsDate])

  // ── Scroll to current hour on view change ─────────────────────────────────

  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (view !== 'month' && scrollRef.current) {
      const currentHour = new Date().getHours()
      scrollRef.current.scrollTop = Math.max(0, currentHour * HOUR_HEIGHT - 100)
    }
  }, [view, currentDate])

  // ── View logic ────────────────────────────────────────────────────────────

  const isMonthView = view === 'month'
  const isWeekView = view === 'week'

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 })
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 })
  const daysToShow = isWeekView
    ? eachDayOfInterval({ start: weekStart, end: weekEnd })
    : [currentDate]

  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 })
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })
  const monthDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd })

  const hours: { val: number; label: string }[] = []
  for (let i = START_HOUR; i <= END_HOUR; i++) {
    const ampm = i >= 12 ? 'PM' : 'AM'
    const disp = i % 12 || 12
    hours.push({ val: i, label: `${disp} ${ampm}` })
  }

  const todayStr = format(new Date(), 'yyyy-MM-dd')
  const currentHourNow = new Date().getHours()

  // ── Navigation ────────────────────────────────────────────────────────────

  const handlePrevDay = () => setCurrentDate(
    isMonthView ? subMonths(currentDate, 1) :
      isWeekView ? subWeeks(currentDate, 1) :
        subDays(currentDate, 1)
  )
  const handleNextDay = () => setCurrentDate(
    isMonthView ? addMonths(currentDate, 1) :
      isWeekView ? addWeeks(currentDate, 1) :
        addDays(currentDate, 1)
  )

  // ── Event handlers ────────────────────────────────────────────────────────

  const handleAddEvent = (evt: Omit<CalendarEvent, 'id'>) => {
    setLocalEvents(prev => [
      ...prev,
      { ...evt, id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` },
    ])
  }

  const handleDateClick = (dateStr: string) => {
    const todayStr = format(new Date(), 'yyyy-MM-dd')
    if (dateStr < todayStr) {
      setDayEventsDate(dateStr)
      setDayEventsOpen(true)
      return
    }
    setNewStartTime('10:00')
    setNewEndTime('11:00')
    setModalDateStr(dateStr)
    setModalOpen(true)
  }

  const handleGridClick = (hourVal: number, dayObj: Date) => {
    const dayStr = format(dayObj, 'yyyy-MM-dd')
    const todayStr = format(new Date(), 'yyyy-MM-dd')
    
    if (dayStr < todayStr) {
      setDayEventsDate(dayStr)
      setDayEventsOpen(true)
      return
    }
    
    if (dayStr === todayStr) {
      const now = new Date()
      const currentHour = now.getHours()
      if (hourVal < currentHour) {
        setDayEventsDate(dayStr)
        setDayEventsOpen(true)
        return
      }
    }

    const endHour = Math.min(hourVal + 1, 23)
    setNewStartTime(`${hourVal.toString().padStart(2, '0')}:00`)
    setNewEndTime(`${endHour.toString().padStart(2, '0')}:00`)
    setModalDateStr(dayStr)
    setModalOpen(true)
  }

  const handleDeleteEvent = (e: MouseEvent, id: string) => {
    e.stopPropagation()
    setLocalEvents(prev => prev.filter(ev => ev.id !== id))
  }

  const handleEventClick = (e: MouseEvent, event: CalendarEvent) => {
    e.stopPropagation()
    setSelectedEvent(event)
    setDetailOpen(true)
  }

  const openNewEventModal = () => {
    const dayStr = format(currentDate, 'yyyy-MM-dd')
    const todayStr = format(new Date(), 'yyyy-MM-dd')
    if (dayStr < todayStr) {
      toast.error("Cannot schedule events for past dates")
      return
    }
    setNewStartTime('10:00')
    setNewEndTime('11:00')
    setModalDateStr(dayStr)
    setModalOpen(true)
  }

  // ── Render events inside a day column (with overlap handling) ─────────────

  const renderEventsForDay = (targetDay: Date, isWeek = false) => {
    const targetDateStr = format(targetDay, 'yyyy-MM-dd')
    const dayEvents = events.filter(e => e.date === targetDateStr)

    const valid = dayEvents
      .map(e => ({ ...e, startDec: parseTimeToDecimal(e.startTime), endDec: parseTimeToDecimal(e.endTime) }))
      .filter(e => e.endDec > START_HOUR && e.startDec < END_HOUR + 1)

    valid.sort((a, b) => a.startDec - b.startDec || a.endDec - b.endDec)

    // Cluster overlapping events
    const clusters: typeof valid[] = []
    let currentCluster: typeof valid = []
    let clusterEnd = -1
    for (const ev of valid) {
      if (ev.startDec >= clusterEnd) {
        if (currentCluster.length) clusters.push(currentCluster)
        currentCluster = [ev]
        clusterEnd = ev.endDec
      } else {
        currentCluster.push(ev)
        clusterEnd = Math.max(clusterEnd, ev.endDec)
      }
    }
    if (currentCluster.length) clusters.push(currentCluster)

    return clusters.flatMap(cluster => {
      // Assign events to non-overlapping columns
      const columns: typeof valid[] = []
      for (const ev of cluster) {
        let placed = false
        for (const col of columns) {
          if (ev.startDec >= col[col.length - 1].endDec) { col.push(ev); placed = true; break }
        }
        if (!placed) columns.push([ev])
      }

      const numCols = columns.length
      return columns.flatMap((col, colIndex) =>
        col.map(ev => {
          const offsetHours = Math.max(0, ev.startDec - START_HOUR)
          const durationHours = Math.max(0.25, ev.endDec - ev.startDec)
          const top = offsetHours * HOUR_HEIGHT + TOP_OFFSET
          const height = durationHours * HOUR_HEIGHT
          const widthPct = 100 / numCols
          const leftPct = colIndex * widthPct
          const pad = isWeek ? 2 : 4

          return (
            <Box
              key={ev.id}
              onClick={e => handleEventClick(e, ev)}
              sx={{
                position: 'absolute',
                top, height,
                left: `calc(${leftPct}% + ${pad}px)`,
                width: `calc(${widthPct}% - ${pad * 2}px)`,
                bgcolor: ev.isDone ? '#f9fafb' : ev.color,
                borderRadius: '4px',
                p: isWeek ? '3px 5px' : '6px 10px',
                display: 'flex', flexDirection: 'column', gap: 0.25,
                borderLeft: `3px solid ${ev.isDone ? '#9ca3af' : ev.colorDark}`,
                opacity: ev.isDone ? 0.65 : 1,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                cursor: 'pointer',
                pointerEvents: 'auto',
                zIndex: 3 + colIndex,
                overflow: 'hidden',
                transition: 'filter 0.15s',
                '&:hover': { filter: 'brightness(0.93)', zIndex: 20 },
                '&:hover .del-btn': { opacity: 1 },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography
                  fontSize={isWeek ? '9px' : '11px'}
                  fontWeight={700}
                  sx={{
                    color: ev.isDone ? '#9ca3af' : ev.colorDark, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    textDecoration: ev.isDone ? 'line-through' : 'none',
                  }}
                >
                  {ev.title}
                </Typography>
                {!ev.readonly && !ev.isDone && (
                  <IconButton
                    className="del-btn" size="small"
                    onClick={e => handleDeleteEvent(e, ev.id)}
                    sx={{ p: 0, opacity: 0, transition: 'opacity 0.15s', color: ev.colorDark, flexShrink: 0 }}
                  >
                    <CloseIcon sx={{ fontSize: isWeek ? 9 : 12 }} />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography fontSize={isWeek ? '8px' : '10px'} fontWeight={600} sx={{ color: ev.isDone ? '#9ca3af' : ev.colorDark, opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  {formatTimeDisplay(ev.startTime)}{!isWeek && ` – ${formatTimeDisplay(ev.endTime)}`}
                </Typography>
                {!isWeek && ev.source && ev.source !== 'CALENDAR' && (
                  <Typography fontSize="8px" fontWeight={700} sx={{
                    color: ev.source === 'LEAD' ? '#2563eb' : '#ca8a04',
                    bgcolor: ev.source === 'LEAD' ? '#eff6ff' : '#fefce8',
                    px: 0.5, borderRadius: '3px', lineHeight: 1.4, flexShrink: 0,
                  }}>
                    {ev.source}
                  </Typography>
                )}
                {(() => {
                  const cat = getCategoryChipConfig(ev);
                  return (
                    <Typography fontSize="8px" fontWeight={700} sx={{
                      color: cat.color,
                      bgcolor: cat.bg,
                      px: 0.5, borderRadius: '3px', lineHeight: 1.4, flexShrink: 0,
                    }}>
                      {cat.label}
                    </Typography>
                  );
                })()}
                {(() => {
                  const priority = ev.priority || ev.originalData?.priority;
                  const pConfig = getPriorityChipConfig(priority);
                  if (!pConfig) return null;
                  return (
                    <Typography fontSize="8px" fontWeight={700} sx={{
                      color: pConfig.color,
                      bgcolor: pConfig.bg,
                      px: 0.5, borderRadius: '3px', lineHeight: 1.4, flexShrink: 0,
                    }}>
                      {pConfig.label}
                    </Typography>
                  );
                })()}
              </Box>
            </Box>
          )
        })
      )
    })
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fdfdfd' }}>
      <Box sx={{
        flex: 1,
        minHeight: 0,
        m: 3,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        border: `1px solid ${themeColors.border}`,
        borderRadius: '8px',
        overflow: 'hidden',
      }}>
      {/* ── Toolbar ── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
        alignItems: 'center',
        gap: 2,
        px: 3,
        py: 1.25,
        borderTop: `1px solid ${themeColors.border}`,
        borderBottom: `1px solid ${themeColors.border}`,
        bgcolor: '#fff',
        flexShrink: 0,
        overflowX: 'auto',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, whiteSpace: 'nowrap' }}>
          {/* View selector */}
          <Select
            size="small" value={view} onChange={e => setView(e.target.value)}
            sx={{
              bgcolor: '#F2EEF9', color: themeColors.primary, fontWeight: 600, fontSize: '13px', borderRadius: '4px',
              height: 36,
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiSelect-icon': { color: themeColors.primary },
            }}
            startAdornment={<EventIcon sx={{ fontSize: 16, ml: 1, color: themeColors.primary }} />}
          >
            <MenuItem value="day">Day</MenuItem>
            <MenuItem value="week">Week</MenuItem>
            <MenuItem value="month">Month</MenuItem>
          </Select>

          {/* Date picker */}
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <Box sx={{
              bgcolor: '#F2EEF9', color: themeColors.primary, fontWeight: 600, fontSize: '13px',
              borderRadius: '4px', px: 2, py: 1, display: 'flex', alignItems: 'center', minHeight: '36px', gap: 1,
            }}>
              {isMonthView
                ? format(currentDate, 'MMMM yyyy')
                : isWeekView
                  ? `${format(weekStart, 'dd')} – ${format(weekEnd, 'dd MMM, yyyy')}`
                  : format(currentDate, 'dd MMM, yyyy')}
              <ArrowDownIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box
              component="input" type="date"
              value={format(currentDate, 'yyyy-MM-dd')}
              onChange={(e: any) => {
                if (e.target.value) {
                  const [y, m, d] = e.target.value.split('-')
                  setCurrentDate(new Date(Number(y), Number(m) - 1, Number(d)))
                }
              }}
              onClick={(e: any) => { try { e.target.showPicker() } catch { } }}
              sx={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                opacity: 0, cursor: 'pointer', zIndex: 10,
                '&::-webkit-calendar-picker-indicator': {
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  width: '100%', height: '100%', opacity: 0, cursor: 'pointer',
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
            <Tooltip title="Previous">
              <IconButton size="small" onClick={handlePrevDay} sx={{ color: '#111827' }}><PrevIcon sx={{ fontSize: 18 }} /></IconButton>
            </Tooltip>
            <Tooltip title="Next">
              <IconButton size="small" onClick={handleNextDay} sx={{ color: '#111827' }}><NextIcon sx={{ fontSize: 18 }} /></IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Typography fontWeight={700} fontSize="16px" color="#111827" sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
          {format(currentDate, 'MMMM, yyyy')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, minWidth: 0, whiteSpace: 'nowrap' }}>
          {/* Filters — same unified dialog as the Leads page */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
            onClick={() => setFilterOpen(true)}
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              height: 36,
              px: 1.5,
              color: activeFilterCount > 0 ? themeColors.primary : '#64748b',
              borderColor: activeFilterCount > 0 ? themeColors.primary : '#e2e8f0',
              bgcolor: activeFilterCount > 0 ? `${themeColors.primary}08` : '#fff',
              '&:hover': { borderColor: themeColors.primary, bgcolor: `${themeColors.primary}08` },
            }}
          >
            Filters
            {activeFilterCount > 0 && (
              <Box component="span" sx={{
                ml: 1, minWidth: 18, height: 18, borderRadius: '9px',
                bgcolor: themeColors.primary, color: '#fff',
                fontSize: '10px', fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', px: '5px',
              }}>
                {activeFilterCount}
              </Box>
            )}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<EventIcon sx={{ fontSize: 16 }} />}
            onClick={openNewEventModal}
            sx={{
              bgcolor: themeColors.primaryDark,
              color: '#fff',
              fontWeight: 600,
              height: 36,
              borderRadius: '4px',
              textTransform: 'none',
              boxShadow: 'none',
              px: 1.5,
              '&:hover': { bgcolor: themeColors.primary, boxShadow: 'none' },
            }}
          >
            New Event
          </Button>
        </Box>
      </Box>

      {/* ── Calendar Grid ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Sticky column headers */}
        {isMonthView ? (
          <Box sx={{ display: 'flex', borderBottom: `1px solid ${themeColors.border}`, bgcolor: '#fdfdfd' }}>
            {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((d, i) => (
              <Box key={d} sx={{ flex: 1, py: 1.5, pl: 2, borderRight: i < 6 ? `1px solid ${themeColors.border}` : 'none' }}>
                <Typography fontSize="12px" fontWeight={600} color="text.secondary" textTransform="uppercase">{d}</Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', borderBottom: `1px solid ${themeColors.border}`, bgcolor: '#fdfdfd' }}>
            {/* Time-axis label */}
            <Box sx={{ width: TIME_COLUMN_WIDTH, flexShrink: 0, borderRight: `1px solid ${themeColors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography fontSize="10px" fontWeight={600} color="text.secondary" textAlign="center" lineHeight={1.3}>
                EST<br />GMT-5
              </Typography>
            </Box>
            {/* Day headers — same flex: 1 structure as the grid below */}
            <Box sx={{ flex: 1, height: 60, display: 'flex' }}>
              {daysToShow.map((day, idx) => {
                const isTodayH = isWeekView && isSameDay(day, new Date())
                return (
                  <Box key={idx} sx={{
                    flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', pl: 2,
                    borderRight: idx < daysToShow.length - 1 ? `1px solid ${themeColors.border}` : 'none',
                    bgcolor: isTodayH ? '#F4F0FD' : 'transparent',
                  }}>
                    <Typography fontSize="11px" fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing="0.5px">
                      {format(day, 'EEE')}
                    </Typography>
                    <Typography fontSize="20px" fontWeight={800} color={isTodayH ? themeColors.primary : 'text.primary'} lineHeight={1}>
                      {format(day, 'dd')}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Box>
        )}

        {/* Scrollable body */}
        {isMonthView ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', bgcolor: '#fff' }}>
            {Array.from({ length: monthDays.length / 7 }).map((_, weekIdx) => {
              const weekDays = monthDays.slice(weekIdx * 7, (weekIdx + 1) * 7)
              return (
                <Box key={weekIdx} sx={{ display: 'flex', flex: 1, minHeight: '120px', borderBottom: `1px solid ${themeColors.border}` }}>
                  {weekDays.map((day, dayIdx) => {
                    const isToday = format(day, 'yyyy-MM-dd') === todayStr
                    const isThisMonth = day.getMonth() === currentDate.getMonth()
                    const dateStr = format(day, 'yyyy-MM-dd')
                    // Sort: undone first (by time), then done/cancelled last
                    const dayEvts = events
                      .filter(e => e.date === dateStr)
                      .sort((a, b) => {
                        const aDone = a.isDone ? 1 : 0
                        const bDone = b.isDone ? 1 : 0
                        if (aDone !== bDone) return aDone - bDone
                        return parseTimeToDecimal(a.startTime) - parseTimeToDecimal(b.startTime)
                      })

                    return (
                      <Box
                        key={dayIdx}
                        onClick={() => handleDateClick(dateStr)}
                        sx={{
                          flex: 1,
                          borderRight: dayIdx < 6 ? `1px solid ${themeColors.border}` : 'none',
                          bgcolor: isToday ? '#F4F0FD' : isThisMonth ? '#fff' : '#f9f9f9',
                          p: 1, display: 'flex', flexDirection: 'column', cursor: 'pointer',
                          transition: 'background-color 0.15s',
                          '&:hover': { bgcolor: isToday ? '#EDE9FB' : '#f4f6f8' },
                        }}
                      >
                        {/* Date number */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                          <Box sx={{
                            width: 24, height: 24, borderRadius: '50%',
                            bgcolor: isToday ? themeColors.primary : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Typography fontSize="13px" fontWeight={isToday ? 800 : 500}
                              color={isToday ? '#fff' : isThisMonth ? 'text.primary' : 'text.disabled'}>
                              {format(day, 'd')}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Events in day cell */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', overflowY: 'auto', flex: 1 }}>
                          {dayEvts.map(ev => (
                            <Box
                              key={ev.id}
                              onClick={e => handleEventClick(e, ev)}
                              sx={{
                                bgcolor: ev.isDone ? '#f9fafb' : ev.color,
                                borderLeft: `3px solid ${ev.isDone ? '#9ca3af' : ev.colorDark}`,
                                borderRadius: '4px', p: '3px 6px',
                                opacity: ev.isDone ? 0.65 : 1,
                                cursor: 'pointer', position: 'relative',
                                '&:hover .del-btn-m': { opacity: 1 },
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography fontSize="10px" fontWeight={700} sx={{
                                  color: ev.isDone ? '#9ca3af' : ev.colorDark, lineHeight: 1.3,
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                                  textDecoration: ev.isDone ? 'line-through' : 'none',
                                }}>
                                  {ev.title}
                                </Typography>
                                {!ev.readonly && !ev.isDone && (
                                  <IconButton
                                    className="del-btn-m" size="small"
                                    onClick={e => handleDeleteEvent(e, ev.id)}
                                    sx={{ p: 0, opacity: 0, transition: 'opacity 0.15s', color: ev.colorDark, ml: 0.25 }}
                                  >
                                    <CloseIcon sx={{ fontSize: 10 }} />
                                  </IconButton>
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography fontSize="9px" fontWeight={600} sx={{ color: ev.isDone ? '#9ca3af' : ev.colorDark, opacity: 0.8 }}>
                                  {formatTimeDisplay(ev.startTime).toLowerCase()}
                                </Typography>
                                {ev.source && ev.source !== 'CALENDAR' && (
                                  <Typography fontSize="8px" fontWeight={700} sx={{
                                    color: ev.source === 'LEAD' ? '#2563eb' : '#ca8a04',
                                    bgcolor: ev.source === 'LEAD' ? '#eff6ff' : '#fefce8',
                                    px: 0.5, borderRadius: '3px', lineHeight: 1.4,
                                  }}>
                                    {ev.source}
                                  </Typography>
                                )}
                                {(() => {
                                  const cat = getCategoryChipConfig(ev);
                                  return (
                                    <Typography fontSize="8px" fontWeight={700} sx={{
                                      color: cat.color,
                                      bgcolor: cat.bg,
                                      px: 0.5, borderRadius: '3px', lineHeight: 1.4,
                                    }}>
                                      {cat.label}
                                    </Typography>
                                  );
                                })()}
                                {(() => {
                                  const priority = ev.priority || ev.originalData?.priority;
                                  const pConfig = getPriorityChipConfig(priority);
                                  if (!pConfig) return null;
                                  return (
                                    <Typography fontSize="8px" fontWeight={700} sx={{
                                      color: pConfig.color,
                                      bgcolor: pConfig.bg,
                                      px: 0.5, borderRadius: '3px', lineHeight: 1.4,
                                    }}>
                                      {pConfig.label}
                                    </Typography>
                                  );
                                })()}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              )
            })}
          </Box>
        ) : (
          /* Week / Day scrollable timeline */
          <Box ref={scrollRef} sx={{ flex: 1, display: 'flex', overflowY: 'auto', overflowX: 'hidden' }}>
            {/* Time axis */}
            <Box sx={{
              width: TIME_COLUMN_WIDTH, flexShrink: 0, borderRight: `1px solid ${themeColors.border}`,
              position: 'relative', height: hours.length * HOUR_HEIGHT + 60, bgcolor: '#ffffff', zIndex: 5,
            }}>
              {hours.map((h, i) => (
                <Box key={h.val} sx={{
                  position: 'absolute', top: i * HOUR_HEIGHT + TOP_OFFSET,
                  width: '100%', display: 'flex', justifyContent: 'center', transform: 'translateY(-50%)',
                }}>
                  <Typography fontSize="10px" fontWeight={500} color="text.secondary">{h.label}</Typography>
                </Box>
              ))}
            </Box>

            {/* Day columns */}
            <Box sx={{ flex: 1, position: 'relative', display: 'flex', height: hours.length * HOUR_HEIGHT + 60 }}>
              {daysToShow.map((day, colIdx) => {
                const isTodayCol = format(day, 'yyyy-MM-dd') === todayStr
                return (
                  <Box key={colIdx} sx={{
                    flex: 1, position: 'relative',
                    borderRight: colIdx < daysToShow.length - 1 ? `1px solid ${themeColors.border}` : 'none',
                    bgcolor: '#ffffff',
                    overflow: 'hidden',
                  }}>
                    {/* Hour slots (clickable) */}
                    {hours.map((h, i) => {
                      const isCurrentSlot = isTodayCol && h.val === currentHourNow
                      return (
                        <Box
                          key={h.val}
                          onClick={() => handleGridClick(h.val, day)}
                          sx={{
                            position: 'absolute', top: i * HOUR_HEIGHT + TOP_OFFSET,
                            left: 0, right: 0, height: HOUR_HEIGHT,
                            borderTop: `1px solid ${themeColors.border}`, zIndex: 1,
                            bgcolor: isCurrentSlot ? `${themeColors.primary}18` : 'transparent',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: isCurrentSlot ? `${themeColors.primary}28` : 'rgba(92,53,216,0.04)' },
                          }}
                        />
                      )
                    })}

                    {/* Current time indicator */}
                    {isTodayCol && (() => {
                      const now = new Date()
                      const pct = (now.getHours() + now.getMinutes() / 60 - START_HOUR) * HOUR_HEIGHT + TOP_OFFSET
                      return (
                        <Box sx={{ position: 'absolute', top: pct, left: 0, right: 0, zIndex: 4, pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444', flexShrink: 0, ml: '-4px' }} />
                          <Box sx={{ flex: 1, height: '2px', bgcolor: '#ef4444' }} />
                        </Box>
                      )
                    })()}

                    {/* Events */}
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 3, pointerEvents: 'none' }}>
                      {renderEventsForDay(day, isWeekView)}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Modals ── */}
      </Box>
      <UnifiedFilterDialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={setFilters}
        filterDefs={filterDefs}
        initialFilters={filters}
      />
      <CreateEventModal
        open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleAddEvent}
        defaultStartTime={newStartTime} defaultEndTime={newEndTime} defaultDate={modalDateStr}
      />
      <EventDetailModal
        open={detailOpen} onClose={() => { setDetailOpen(false); setSelectedEvent(null) }} event={selectedEvent}
      />
      <DayEventsModal
        open={dayEventsOpen}
        onClose={() => setDayEventsOpen(false)}
        dateStr={dayEventsDate}
        events={dayEvents}
        onEventClick={(ev) => {
          setSelectedEvent(ev)
          setDetailOpen(true)
        }}
      />
    </Box>
  )
}
